import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/** Each action resolves to an error message to show, or `null` when it worked. */
interface OtpFlowActions {
    send: () => Promise<string | null>;
    verify: (code: string) => Promise<string | null>;
    onVerified?: () => void | Promise<void>;
}

/** Code entry shared by the paid signup steps: sends once on mount, then verify and resend. */
export function useOtpFlow({ send, verify, onVerified }: OtpFlowActions) {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const sentRef = useRef(false);
    const sendRef = useRef(send);
    sendRef.current = send;

    useEffect(() => {
        if (sentRef.current) return;
        sentRef.current = true;
        sendRef.current().then(message => {
            if (message) setError(message);
        });
    }, []);

    const submit = async () => {
        if (code.length !== 6 || loading) return;
        setLoading(true);
        setError(null);
        try {
            const message = await verify(code);
            if (message) {
                setError(message);
                setCode('');
                return;
            }
            await onVerified?.();
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        setLoading(true);
        setError(null);
        try {
            const message = await send();
            if (message) {
                setError(message);
                return;
            }
            setCode('');
            toast.success('Nuevo código enviado');
        } finally {
            setLoading(false);
        }
    };

    return { code, setCode, loading, setLoading, error, setError, submit, resend };
}
